import * as JSZip from 'jszip';
import { format as formatDate } from 'date-fns';
import { safeAtob, safeBtoa } from 'pxc-editor-core/helpers/common';
import { getCanvas as getBarcodeCanvas } from 'pxc-editor-core/helpers/barcode';
import backgroundPrintFunc from 'pxc-editor-material-svg-rendering/backgroundPrintFunc';
import { PRINTING_MODE } from 'pxc-editor-material-svg-rendering/constants';
import { printSvgToImage } from 'pxc-editor-material-svg-rendering/helpers/print';
import { SymbolController } from 'pxc-editor-data/Symbol';
import { parseInitProjectData } from 'pxc-editor-web/environment_modules/xcore/helpers/parser';
import { generateDataToDownload } from 'pxc-editor-web/environment_modules/xcore/helpers/presenter';
import store from '@/store';
import { colors } from '@/globalStyles';
import { REQUEST_STATUS, INTERACTIVE_SVG_ID } from '@/constants';
import {
  handleEventFileUpload,
  saveToDevice,
  urlDataToBlob,
  removeProjectFromLocalStorage,
  getElementByGetElementById,
  getAssetsFullPath,
  getTextFieldMatrices,
} from '@/helpers';
import addQuietZoneToGraphicContent from '@/helpers/addQuietZoneToGraphicContent';
import { getBuildedMatfileTemplate } from '@/helpers/matfile';
import { paginationGoToPage, clearAllSelectedSections, setCmmeStates } from '@/actions';
import { setAddToCartStatus } from '@/store/actions/cmme';
import { setShowLoader } from '@/actions/main';
import { setLastSavedPages } from '@/actions/page';
import { uploadProject } from '@/actions/project';
import WebComponent from '@/endpoints/webComponent';

export const handleProjectFileUpload = e => async dispatch => {
  const file = e.target?.files?.[0];
  const extArray = file.name.split('.');
  const ext = extArray?.length > 1 ? extArray[extArray.length - 1].toLowerCase() : '';

  if (file && ext === 'cmme') {
    const fileData = {};
    const f = e.target.files[0];

    try {
      const zip = await JSZip.loadAsync(f);

      dispatch(setShowLoader(true));

      const filesInside = Object.keys(zip.files);
      const countOfFiles = filesInside.reduce((count, fileName) => {
        if (['project-metadata.json', 'project.json', 'material.json', 'wizard.json', 'material-metadata.json'].includes(fileName)) {
          return count + 1;
        }
        return count;
      }, 0);

      let counter = 0;
      const filePromises = filesInside.map(async relativePath => {
        const zipEntry = zip.files[relativePath];
        const zipExtArray = zipEntry.name.split('.');
        const zipExt = zipExtArray?.length > 1 ? zipExtArray[extArray.length - 1].toLowerCase() : '';
        const zipName = zipExtArray?.length > 1 ? zipExtArray[extArray.length - 2].toLowerCase().replace('-', '_') : '';

        if (zipExt === 'json' && ['project', 'project_metadata', 'material', 'wizard', 'material_metadata'].includes(zipName)) {
          counter += 1;

          const content = await zipEntry.async('blob');
          const arrayBuffer = await content.arrayBuffer();
          const enc = new TextDecoder('utf-8');
          const dec = enc.decode(arrayBuffer);
          let data = dec;

          switch (zipName) {
            case 'project':
              data = safeBtoa(JSON.stringify(JSON.parse(dec)));
              break;
            case 'project_metadata':
              data = JSON.parse(dec);
              data = data.project_metadata ? data.project_metadata : data;
              break;
            case 'material':
              data = JSON.parse(safeAtob(dec));
              data = data.material ? data.material : data;
              data = {
                name: data.general.file_name,
                contents: JSON.stringify(data),
                is_base64: false,
              };
              break;
            case 'wizard':
              data = safeBtoa(JSON.stringify(JSON.parse(dec)));
              break;
            case 'material_metadata':
              data = JSON.parse(safeAtob(dec));
              data = data.material_metadata ? data.material_metadata : data;
              data.is_base64 = false;
              data['contents'] = JSON.stringify(data);
              break;
            default:
              data = dec;
              break;
          }

          fileData[zipName] = data;

          if (counter >= countOfFiles) {
            fileData['preview'] = null;
            fileData['save_as'] = true;
          }
        }
      });

      await Promise.all(filePromises);

      const parsedProjectData = parseInitProjectData(fileData);

      dispatch(uploadProject({ projectData: parsedProjectData }));
      dispatch(setShowLoader(false));
    } catch (error) {
      console.error('Error processing the ZIP file:', error);
      dispatch(setShowLoader(false));
    }
  } else {
    dispatch(setShowLoader(true));
    handleEventFileUpload(e, (fileData, fileName) => {
      const project = JSON.parse(fileData);
      const parsedProjectData = parseInitProjectData({ ...project, fileName });

      dispatch(uploadProject({ projectData: parsedProjectData }));
      dispatch(setShowLoader(false));
    });
  }
};

export const handleMatfileUpload = syntheticEvent => (dispatch, getState) => {
  dispatch(setShowLoader(true));
  handleEventFileUpload(syntheticEvent, (fileData, fileName) => {
    const parsedProjectData = parseInitProjectData({
      material: {
        name: fileName,
        contents: fileData,
        is_base64: false,
      },
    });

    dispatch(
      uploadProject({
        projectData: parsedProjectData,
        beforeFinishActionsClb: () => dispatch(setShowLoader(false)),
      }),
    );
  });
};

export const handleSaveProjectClick = projectName => dispatch => {
  dispatch(setShowLoader(true));
  setTimeout(() => {
    dispatch(saveProject(projectName));
  });
};

export const handleCustomerProjectClick = ({ filename, accounts }) => dispatch => {
  dispatch(setAddToCartStatus(REQUEST_STATUS.LOADING));
  setTimeout(() => {
    dispatch(saveCustomerProject({ filename, accounts }));
  }, 0);
};

export const handleCustomerProjectDebugClick = () => (dispatch, getState) => {
  dispatch(saveCustomerProjectDebug());
};

const createMetaData = data => {
  const material = data?.material ? data?.material : data;
  if (material.general?.format_version === 1) {
    let article_numbers = [];
    material.variants?.forEach(item => {
      article_numbers.push(item.article_number);
    });
    article_numbers = article_numbers.sort();
    const layouts = [];
    material.layouts?.forEach(item => {
      layouts.push(item.name);
    });
    const meta = {
      name: material.general.name,
      filename: material.general.file_name + '.mat.json',
      revision: material.general.revision,
      type: material.general.type,
      shield_size: material.general.shield_size,
      page_size: {
        w: material.geometry?.page.box.r,
        h: material.geometry?.page.box.b,
      },
      lettering_field_size: material.general.lettering_field_size || material.general.shield_size,
      mounting_type: material.mounting_type || 'Kleben', //'undefined',
      variable_width: false,
      shields_number: material.general.shields_number,
      selected_article_number: article_numbers[0],
      article_numbers: article_numbers,
      variants: material.variants,
      selected_layout: layouts[0],
      layouts: layouts,
    };
    return meta;
  } else if (material.general?.format_version === 2) {
    let article_numbers = [];
    material.variants?.forEach(item => {
      article_numbers.push(item.article_number);
    });
    article_numbers = article_numbers.sort();
    const layouts = [];
    material.layouts?.forEach(item => {
      layouts.push(item.name);
    });
    const meta = {
      name: material.general.name,
      filename: material.general.file_name + '.mat.json',
      revision: material.general.revision,
      type: material.general.type,
      shield_size: material.general.shield_size,
      page_size: {
        w: material.geometry?.page.box.r,
        h: material.geometry?.page.box.b,
      },
      lettering_field_size: material.general.lettering_field_size || material.general.shield_size,
      mounting_type: material.mounting_type || 'Kleben', //'undefined',
      variable_width: false,
      shields_number: material.general.shields_number,
      selected_article_number: article_numbers[0],
      article_numbers: article_numbers,
      variants: material.variants,
      selected_layout: layouts[0],
      layouts: layouts,
    };
    return meta;
  } else {
    return null;
  }
};

const jsonToZipFilePromise = async data => {
  const zip = new JSZip();
  if (data) {
    if (data.project) {
      zip.file('project.json', safeAtob(data.project));
    }
    if (data.project_metadata) {
      if (data.project_metadata?.material_metadata) {
        zip.file('material-metadata.json', safeBtoa(JSON.stringify(data.project_metadata.material_metadata)));
      } else {
        const matfile = JSON.parse(data.material.contents);
        const material_metadata = createMetaData(matfile);
        if (material_metadata) {
          data.project_metadata.material_metadata = material_metadata;
          zip.file('material-metadata.json', safeBtoa(JSON.stringify(material_metadata)));
        }
      }
      zip.file('project-metadata.json', JSON.stringify(data.project_metadata));
    }
    if (data.material?.contents) {
      zip.file('material.json', safeBtoa(JSON.stringify(JSON.parse(data.material.contents))));
    }
    if (data.preview) {
      await urlDataToBlob(data.preview).then(blob => zip.file('preview.png', blob));
    }
    if (data.wizard?.application_type) {
      zip.file('wizard.json', safeAtob(data.wizard));
    }
    if (!!data?.resources?.barcodes.length) {
      const barcodesBlobs = await Promise.all(
        data.resources.barcodes.map(barcode => {
          return {
            name: barcode.name,
            blob: barcode.blob,
          };
        }),
      );
      barcodesBlobs.forEach(({ name, blob }) => {
        zip.file(`resources/barcodes/${name}.png`, blob);
      });
    }
    if (!!data?.resources?.svgSymbols.length) {
      const svgSymbolsBlobs = await Promise.all(
        data.resources.svgSymbols.map(async svgSymbol => {
          return {
            name: svgSymbol.name,
            blob: svgSymbol.blob,
          };
        }),
      );

      svgSymbolsBlobs.forEach(({ name, blob }) => {
        zip.file(`resources/svg-symbols/${name}`, blob);
      });
    }
    if (!!data?.resources?.textFieldMatrices.length) {
      const textFieldMatricesBlobs = await Promise.all(
        data.resources.textFieldMatrices.map(async matrix => {
          return {
            name: `${matrix.name}`,
            blob: await urlDataToBlob(matrix.image),
          };
        }),
      );

      textFieldMatricesBlobs.forEach(({ name, blob }) => {
        zip.file(`resources/text-field-matrix/${name}`, blob);
      });
    }
    if (data.project_version) {
      zip.file('version.json', JSON.stringify(data.project_version));
    }
    return zip.generateAsync({ type: 'blob' });
  }
  return Promise.reject(new Error('No data found'));
};

const getAllSections = () => {
  const { core: { pagesOrder, pages } } = store.getState();
  return pagesOrder.reduce((acc, pageUid) => {
    const page = pages[pageUid];
    const sections = Object.values(page.sections);
    return [...acc, ...sections];
  }, []);
};

const getBarcodeImages = async sections => {
  const barcodes = [];

  for (const section of sections) {
    if (section.code) {
      const { id, code } = section;
      const { text, ...barcodeOptions } = code;
      const canvas = getBarcodeCanvas(text, barcodeOptions);
      const url = canvas.toDataURL();

      if (section.graphicFormattingScale === 1) {
        const blob = urlDataToBlob(url);

        barcodes.push({ name: id, blob });
      } else {
        const image = await addQuietZoneToGraphicContent({ src: url });
        const blob = urlDataToBlob(image);

        barcodes.push({ name: id, blob });
      }
    }
  }

  return barcodes;
};

const getSvgSymbolsImages = async sections => {
  const svgSymbols = {};

  for (const section of sections) {
    if (section.glyph) {
      const { symbolFont, glyph } = section;
      const fileName = SymbolController.getSymbolUrl({ symbolFont, unicode: glyph.unicode });

      if (!svgSymbols[fileName]) {
        const url = getAssetsFullPath(fileName);

        if (section.graphicFormattingScale === 1) {
          const blob = await urlDataToBlob(url);

          svgSymbols[fileName] = { name: fileName, blob };
        } else {
          const svgString = await addQuietZoneToGraphicContent({ src: url, type: 'svg' });
          const blob = new Blob([svgString], { type: 'image/svg+xml' });

          svgSymbols[fileName] = { name: fileName, blob };
        }
      }
    }
  }

  return Object.values(svgSymbols);
};

const getResources = async () => {
  const sections = getAllSections();
  const barcodes = await getBarcodeImages(sections);
  const svgSymbols = await getSvgSymbolsImages(sections);

  return {
    barcodes,
    svgSymbols,
  };
};

const saveProject = (projectName) => (dispatch, getState) => {
  const {
    core: {
      pagesOrder, pages, paperColor, isContentPlacingDebugging, isDebuggingGrid, paginationPageNum, cmmeStates
    }
  } = getState();
  const matfileTemplate = getBuildedMatfileTemplate();
  const { printParameters: materialPrintParameters, type: materialType } = matfileTemplate;
  const currentPage = paginationPageNum;
  let previewImg = null;

  dispatch(clearAllSelectedSections());

  backgroundPrintFunc(
    [pagesOrder[0]],
    { mode: PRINTING_MODE.PREVIEW, skipMaterialTransforms: true },
    {
      pages,
      materialPrintParameters,
      materialType,
      paperColor,
      isContentPlacingDebugging,
      isDebuggingGrid,
    },
    ({ imageUrlData }) => {
      previewImg = imageUrlData;
    },
    async () => {
      try {
        const payload = await generateProjectData({ previewImg, getState });
        const project = await jsonToZipFilePromise(payload);

        saveToDevice(project, `${projectName}.cmme`);
        
        const previews = await generateProjectPreviewImage(dispatch, getState);
        const projectPreviews = await previewsToZipFile(previews);

        saveToDevice(projectPreviews, `${projectName} preview.zip`);

        dispatch(setLastSavedPages());
        dispatch(paginationGoToPage(currentPage));
        dispatch(setCmmeStates(cmmeStates));

        removeProjectFromLocalStorage();
      } catch (error) {
        console.error('[saveProject]:', error);
      } finally {
        dispatch(setShowLoader(false));
      }
    },
  );
};

const saveCustomerProject = ({ filename, accounts }) => (dispatch, getState) => {
  const {
    core: {
      pagesOrder, pages, paperColor, isContentPlacingDebugging, isDebuggingGrid, cmmeStates
    },
    cmmeReducer: { config: webComponentConfing }
  } = getState();
  const { printParameters: materialPrintParameters, type: materialType } = getBuildedMatfileTemplate();
  let previewImg = null;

  dispatch(clearAllSelectedSections());

  backgroundPrintFunc(
    [pagesOrder[0]],
    { mode: PRINTING_MODE.PREVIEW, skipMaterialTransforms: true },
    {
      pages,
      materialPrintParameters,
      materialType,
      paperColor,
      isContentPlacingDebugging,
      isDebuggingGrid,
    },
    ({ imageUrlData }) => {
      previewImg = imageUrlData;
    },
    async () => {
      try {
        const payload = await generateProjectData({ previewImg, getState });
        const project = await jsonToZipFilePromise(payload);
        const previews = await generatePreviewImagesToRequest(dispatch, getState);
        const [customer] = accounts;
        const solutionId = await WebComponent.postCreateSolution(webComponentConfing, customer.customerId);

        await WebComponent.postProjectData({ webComponentConfing, createdSolutionId: solutionId, projectData: project, filename });
        await Promise.all(
          previews.map((preview, index) =>
            WebComponent.sendProjectPreview({
              webComponentConfing,
              createdSolutionId: solutionId,
              position: index,
              data: preview,
            }),
          ),
        );
        await WebComponent.putProjectData({ webComponentConfing, createdSolutionId: solutionId, filename });
        await WebComponent.submitSolution(webComponentConfing, solutionId);

        dispatch(setLastSavedPages());
        dispatch(setAddToCartStatus(REQUEST_STATUS.SUCCESS));
        dispatch(setCmmeStates(cmmeStates));

        removeProjectFromLocalStorage();
      } catch (error) {
        dispatch(setAddToCartStatus(REQUEST_STATUS.INITIAL));
        console.error('[saveCustomerProject]:', error);
      } finally {}
    },
  );
};

const saveCustomerProjectDebug = () => (dispatch, getState) => {
  dispatch(setShowLoader(true));

  const { core: { pagesOrder, pages, paperColor, isContentPlacingDebugging, isDebuggingGrid, cmmeStates } } = getState();
  const { name: matfileName, printParameters: materialPrintParameters, type: materialType } = getBuildedMatfileTemplate();
  const filename = `${matfileName} ${formatDate(new Date(), 'yyyy-MM-dd')}`; 
  let previewImg = null;

  dispatch(clearAllSelectedSections());

  backgroundPrintFunc(
    [pagesOrder[0]],
    { mode: PRINTING_MODE.PREVIEW, skipMaterialTransforms: true },
    {
      pages,
      materialPrintParameters,
      materialType,
      paperColor,
      isContentPlacingDebugging,
      isDebuggingGrid,
    },
    ({ imageUrlData }) => {
      previewImg = imageUrlData;
    },
    async () => {
      try {
        const payload = await generateProjectData({ previewImg, getState });
        const project = await jsonToZipFilePromise(payload);
        
        saveToDevice(project, `${filename}.cmme`);

        dispatch(setLastSavedPages());
        dispatch(setCmmeStates(cmmeStates));
      } catch (error) {
        console.error('[saveCustomerProjectDebug]:', error);
      } finally {
        dispatch(setShowLoader(false));
      }
    },
  );
};

const generatePreviewImagesToRequest = async (dispatch, getState) => {
  const previews = await generateProjectPreviewImage(dispatch, getState);
  const files = previews.map(async (item, index) => {
    const blob = await urlDataToBlob(item.imageUrlData);
    return new File([blob], `${index}.png`, { type: 'image/png' });
  });
  return Promise.all(files);
};

const generateProjectPreviewImage = async (dispatch, getState) => {
  const { core: { pagesOrder } } = getState();
  const promises = [];
  const pageList = pagesOrder.slice(0, 20);
  console.log('[generateProjectPreviewImage]', { pageListLength: pageList.length });
  for (const [index, pageUid] of pageList.entries()) {
    const pageNumber = index + 1;

    dispatch(paginationGoToPage(pageNumber, false));

    // TO DO: find a better way to make delay (to load <images/>)
    await new Promise((resolve, reject) => setTimeout(() => resolve(), 1000));

    const rendererSvgNode = getElementByGetElementById(INTERACTIVE_SVG_ID).cloneNode(true);
    const targetGroup = getElementByGetElementById(pageUid);
    const bbox = targetGroup.getBBox();
    const minX = bbox.x;
    const minY = bbox.y;
    const width = bbox.width;
    const height = bbox.height;
    const viewBoxWidth = width;
    const viewBoxHeight = height;
    const xOffset = 5;
    const yOffset = 5;
    const viewBox = `${minX - xOffset} ${minY - yOffset} ${viewBoxWidth + xOffset * 2} ${viewBoxHeight + yOffset * 2}`;

    rendererSvgNode.setAttribute('width', viewBoxWidth);
    rendererSvgNode.setAttribute('height', viewBoxHeight);
    rendererSvgNode.setAttribute('viewBox', viewBox);

    const preview = await printSvgToImage(rendererSvgNode);

    promises.push({ page: pageNumber, ...preview });
  }

  return Promise.all(promises);
};

const previewsToZipFile = async data => {
  const zip = new JSZip();
  const blobs = await Promise.all(
    data.map(item => {
      return { name: item.page, blob: urlDataToBlob(item.imageUrlData) };
    }),
  );

  blobs.forEach(({ name, blob }) => zip.file(`${name}.png`, blob));

  return zip.generateAsync({ type: 'blob' });
};

const generateTextFieldMatrixContent = (getState) => {
  const { core: { pages, paperColor } } = getState();
  const { printParameters: materialPrintParameters, type: materialType } = getBuildedMatfileTemplate();
  const textFieldMatrices = Object.values(pages).reduce((prevPage, nextPage) => {
    const matrixShields = getTextFieldMatrices(nextPage);
    const matrixShieldIds = Object.entries(matrixShields).reduce((acc, [id, settings]) => {
      if (settings.isMatrix) {
        return { ...acc, [id]: settings };
      }
      return acc;
    }, {});
    return { ...prevPage, ...matrixShieldIds };
  }, {});
  const shieldIds = Object.keys(textFieldMatrices);

  return new Promise((resolve, reject) => {
    const data = [];
    if (!!shieldIds.length) {
      backgroundPrintFunc(
        shieldIds,
        { mode: PRINTING_MODE.TEXT_FIELD_MATRIX, fillStyle: colors.transparent },
        { pages, materialPrintParameters, materialType, paperColor, isContentPlacingDebugging: false, isDebuggingGrid: false },
        (renderedData, printObjId, pageNum) => {
          data.push({ name: `${textFieldMatrices[printObjId].fileName}.png`, image: renderedData.imageUrlData });
        },
        () => resolve(data),
      );
    } else {
      resolve(data)
    }
  });
};

const generateProjectData = async ({ previewImg, getState }) => {
  const resources = await getResources();
  const textFieldMatrices = await generateTextFieldMatrixContent(getState);
  const data = generateDataToDownload({ previewImg });

  return {
    ...data,
    resources: { ...resources, textFieldMatrices }
  };
};